import { FC, useContext, useEffect } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePermissionsContext } from "../../context/permissionContext";
import useIdentity from "../../hooks/useIdentity";
import useI18n from "../../hooks/useTranslations";
import { useRolesApi } from "../../http/roles/api";
import { User } from "../../http/users/types";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import MultiCheckbox from "../ui/MultiCheckbox";

export interface UserFormBody {
  roles: string[];
  permissions: string[];
}

interface UserFormProps {
  user?: User;
  onSubmit: (formData: UserFormBody) => void;
  disabled?: boolean;
  isSubmitting?: boolean;
}

const UserForm: FC<UserFormProps> = ({ onSubmit, user, disabled, isSubmitting }: UserFormProps) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const identity = useIdentity();
  const rolesApi = useRolesApi();
  const permissionsContext = usePermissionsContext();

  const defaultValues: UserFormBody = {
    roles: user?.roles ?? [],
    permissions: user?.permissions ?? [],
  };

  const rolesSwr = rolesApi.getAdminRolesAndPermissions();

  useEffect(() => {
    rolesSwr.error && dispatch(showErrorNotification(i18n, rolesSwr.error));
  }, [rolesSwr.error]);

  useEffect(() => {
    permissionsContext.isAdmin && rolesSwr.trigger();
  }, [permissionsContext]);

  return (
    <Form<UserFormBody>
      submitText={i18n.translation.common.save}
      disabled={disabled || isSubmitting}
      defaultValues={defaultValues}
      onSubmit={user && identity && user.id !== identity.id ? () => onSubmit : undefined}
    >
      {({ setValue, watch, reset }) => {
        useEffect(() => {
          reset(defaultValues);
        }, [user]);

        const roles = watch("roles");
        const permissions = watch("permissions");

        return (
          <FormFieldsContainer>
            <FormField label={i18n.translation.common.firstName}>
              <Input readOnly defaultValue={user?.firstName} />
            </FormField>
            <FormField label={i18n.translation.common.lastName}>
              <Input readOnly defaultValue={user?.lastName} />
            </FormField>
            <FormField label={i18n.translation.common.email}>
              <Input readOnly defaultValue={user?.email} />
            </FormField>
            <FormField label={i18n.translation.roles.plural}>
              <MultiCheckbox
                values={
                  rolesSwr.data?.roles.map((x) => ({
                    id: x.value,
                    title: x.value,
                    selected: roles.includes(x.value),
                  })) ?? []
                }
                disabled={user?.id === identity?.id}
                onUpdate={(id, checked) => {
                  setValue("roles", checked ? [...roles, id] : roles.filter((x) => x !== id));
                }}
              />
            </FormField>
            <FormField label={i18n.translation.permissions.plural}>
              <MultiCheckbox
                values={(() => {
                  return (
                    rolesSwr.data?.permissions.map((x) => {
                      const isFromRole = !!rolesSwr.data?.roles
                        .filter((r) => roles.includes(r.value))
                        .flatMap((r) => r.permissions)
                        .filter((p) => p === x).length;

                      return {
                        id: x,
                        title: x,
                        selected: permissions.includes(x) || isFromRole,
                        disabled: isFromRole || (!isFromRole && x === "access"),
                      };
                    }) ?? []
                  );
                })()}
                disabled={user?.id === identity?.id}
                onUpdate={(id, checked) => {
                  setValue("permissions", checked ? [...permissions, id] : permissions.filter((x) => x !== id));
                }}
              />
            </FormField>
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default UserForm;
